// Heart Games Hub
// Main page for browsing and selecting Heart Games - 21 QUESTIONS Style

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHeartGames } from '../../hooks';
import { useAuthContext } from '../../providers';
import { useSavedGames } from '../../hooks/heartGames/useSavedGames';
import { HeartGame, GameCategory } from '../../types/heartGames';
import { SavedGamesModal } from '../../components/HeartGames/SavedGames/SavedGamesModal';
import { GameService } from '../../services/HeartGames/gameService';
import { HeartScore } from '../../types/heartGames';
import { ScoringService } from '../../services/HeartGames/scoringService';
import {
  HeartGamesHubContainer,
  HeartGamesHeader,
  HeartGamesTitle,
  HeaderRightSpacer,
  GamesListContainer,
  GamesList,
  GameCard,
  GameIcon,
  GameCardContent,
  GameCardTitle,
  GameCardDescription,
  FilterBar,
  FilterButton,
  GamesEmptyState,
  GamesLoading,
  HeartProfileCard,
  HeartProfileCardIcon,
  HeartProfileCardContent,
  HeartProfileCardTitle,
  HeartProfileCardText,
} from '../../styles/HeartGames/HeartGamesHub.styles';

export const HeartGamesHub: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { games, isLoading, error } = useHeartGames(user?.id);
  const { isGameSaved, toggleSavedGame, getSavedGames } = useSavedGames(user?.id);
  const [selectedFilter, setSelectedFilter] = useState<GameCategory | 'all'>('all');
  const [isSavedGamesOpen, setIsSavedGamesOpen] = useState(false);
  // The result the user has already earned, if any. Shown above the games so
  // there is a way back to it that does not involve playing anything again.
  const [earnedType, setEarnedType] = useState<string | null>(null);
  // Tracked separately from the games list so the page can wait for BOTH
  // before painting. Rendering as each arrives meant the games appeared first
  // and the profile card dropped in above them a moment later, shoving the
  // whole list down under the reader's cursor. One paint, no jump.
  const [profileChecked, setProfileChecked] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      // Nothing to look up, so nothing to wait for.
      setProfileChecked(true);
      return;
    }
    let cancelled = false;
    GameService.getLatestSession()
      .then((session) => {
        if (cancelled) return;
        const score = session?.score as HeartScore | undefined;
        if (score?.type && score.type !== 'UNKNOWN') setEarnedType(score.type);
      })
      // No result yet is the normal case for a new player, not a failure.
      .catch(() => undefined)
      // Settled either way: a lookup that failed must not hold the page
      // hostage, it just means there is no card to show.
      .finally(() => { if (!cancelled) setProfileChecked(true); });
    return () => { cancelled = true; };
  }, [user?.id]);

  // Get unique categories from games
  const categories = useMemo(() => {
    const cats = new Set<GameCategory>(games.map((g) => g.category));
    return Array.from(cats);
  }, [games]);

  // Filter games based on selected category
  const filteredGames = useMemo(() => {
    if (selectedFilter === 'all') {
      return games;
    }
    return games.filter((game) => game.category === selectedFilter);
  }, [games, selectedFilter]);

  // Handle card click - start game directly
  const handleGameClick = (gameId: string) => {
    navigate(`/heart-games/${gameId}/play`);
  };

  // Handle saved games icon click
  const handleSavedGamesClick = () => {
    setIsSavedGamesOpen(true);
  };

  // Get saved games
  const savedGames = getSavedGames(games);

  // Format category name for display
  const formatCategoryName = (category: string): string => {
    return category
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (isLoading || !profileChecked) {
    return (
      <HeartGamesHubContainer>
        <HeartGamesHeader>
          <HeaderRightSpacer />
          <HeartGamesTitle>HEART GAMES</HeartGamesTitle>
          <HeaderRightSpacer />
        </HeartGamesHeader>
        <GamesLoading>Loading</GamesLoading>
      </HeartGamesHubContainer>
    );
  }

  if (error) {
    return (
      <HeartGamesHubContainer>
        <HeartGamesHeader>
          <HeaderRightSpacer />
          <HeartGamesTitle>HEART GAMES</HeartGamesTitle>
          <HeaderRightSpacer />
        </HeartGamesHeader>
        <GamesEmptyState>
          <h3>Error</h3>
          <p>{error}</p>
        </GamesEmptyState>
      </HeartGamesHubContainer>
    );
  }

  return (
    <>
      <HeartGamesHubContainer>
        <HeartGamesHeader>
          <HeaderRightSpacer />
          <HeartGamesTitle>HEART GAMES</HeartGamesTitle>
          <HeaderRightSpacer />
        </HeartGamesHeader>

        <GamesListContainer>
          {earnedType && (
            <HeartProfileCard
              onClick={() => navigate('/heart-games/profile')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/heart-games/profile');
                }
              }}
              aria-label="View your Heart Profile"
            >
              <HeartProfileCardIcon>🏅</HeartProfileCardIcon>
              <HeartProfileCardContent>
                <HeartProfileCardTitle>Your Heart Profile</HeartProfileCardTitle>
                <HeartProfileCardText>
                  {earnedType} — {ScoringService.getTypeInfo(earnedType).name}. See your traits,
                  insights and who you match with.
                </HeartProfileCardText>
              </HeartProfileCardContent>
            </HeartProfileCard>
          )}

          {filteredGames.length > 0 ? (
            <GamesList>
              {filteredGames.map((game) => (
                <GameCard
                  key={game.id}
                  onClick={() => handleGameClick(game.id)}
                  aria-label={`Play ${game.name}`}
                >
                  <GameIcon $color={game.color}>{game.icon}</GameIcon>
                  <GameCardContent>
                    <GameCardTitle>{game.name}</GameCardTitle>
                    <GameCardDescription>{game.description}</GameCardDescription>
                  </GameCardContent>
                </GameCard>
              ))}
            </GamesList>
          ) : (
            <GamesEmptyState>
              <h3>No games found</h3>
              <p>
                {selectedFilter === 'all'
                  ? 'Check back later for new games.'
                  : `No games in the ${formatCategoryName(selectedFilter)} category.`}
              </p>
            </GamesEmptyState>
          )}
        </GamesListContainer>

        <FilterBar>
          <FilterButton
            $isActive={selectedFilter === 'all'}
            onClick={() => setSelectedFilter('all')}
          >
            All
          </FilterButton>
          {categories.map((category) => (
            <FilterButton
              key={category}
              $isActive={selectedFilter === category}
              onClick={() => setSelectedFilter(category)}
            >
              {formatCategoryName(category)}
            </FilterButton>
          ))}
        </FilterBar>
      </HeartGamesHubContainer>

      <SavedGamesModal
        isOpen={isSavedGamesOpen}
        onClose={() => setIsSavedGamesOpen(false)}
        savedGames={savedGames}
        onGameClick={handleGameClick}
      />
    </>
  );
};
